import React, { useState, useEffect, createContext, useContext } from "react";
import { fetchDummyMRData, createDummyMR, updateDummyMR } from "./dummyData";
import { createMRRecord } from "../../../utils/MRCreate";
import { updateMRRecord } from "../../../utils/MRUpdate";
import { fetchMRData as apiFetchMRData } from "../../../api/FetchMRData";

// Create context for MR data
const MRDataContext = createContext();

export function useMRData() {
  return useContext(MRDataContext);
}

export default function MRDataProvider({ children }) {
  const [mrData, setMRData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Function to fetch MR data
  const getMRData = async () => {
    try {
      console.log("Fetching MR data...");
      setLoading(true);
      setError(null);

      const records = await apiFetchMRData();
      if (Array.isArray(records)) {
        setMRData(records);
        console.log("Data set to state:", records.length, "records");
      } else {
        setError("Received invalid data format");
        setMRData([]);
      }
    } catch (err) {
      console.error("Error fetching MR data:", err);
      setError("Failed to fetch management review data");
      setMRData([]);
    } finally {
      setLoading(false);
    }
  };

  // Function to create a new MR record
  const createMR = async (mrData) => {
    try {
      console.log("Creating new MR with data:", mrData);
      setLoading(true);
      const newMR = await createMRRecord(mrData);
      console.log("Created new MR:", newMR);

      // Wait longer to allow Zoho backend to index the new record
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setRefreshTrigger((prev) => prev + 1);
      return newMR;
    } catch (err) {
      console.error("Error creating MR:", err);
      setError("Failed to create management review");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Function to update an MR record
  const updateMR = async (id, updateData) => {
    try {
      console.log("Updating MR with ID:", id, "Data:", updateData);
      setLoading(true);
      const success = await updateMRRecord(id, updateData);
      console.log("Update result:", success);

      if (success) {
        setRefreshTrigger((prev) => prev + 1);
      }
      return success;
    } catch (err) {
      console.error("Error updating MR:", err);
      setError("Failed to update management review");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on initial load or when refresh is triggered
  useEffect(() => {
    console.log("Refresh trigger changed:", refreshTrigger);
    getMRData();
  }, [refreshTrigger]);

  // Value to be provided to consumers
  const contextValue = {
    mrData,
    loading,
    error,
    fetchMRData: getMRData,
    createMR,
    updateMR,
    refreshTrigger,
    setRefreshTrigger,
  };

  return (
    <MRDataContext.Provider value={contextValue}>
      {children}
    </MRDataContext.Provider>
  );
}
